import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { db, functions } from '../firebaseConfig'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useTheme } from '../context/ThemeContext'

const AddUserForm = () => {
    const navigate = useNavigate()
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [formData, setFormData] = useState({
        nama: '',
        email: '',
        posisi: '',
        validator: [],
        reviewer1: [],
        reviewer2: [],
        unit: [],
        role: '',
        department: [],
        bankName: '',
        accountNumber: '',
        lokasi: [],
        platKendaraan: []
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [reviewerOptions, setReviewerOptions] = useState([])
    const [validatorOptions, setValidatorOptions] = useState([])

    // BAGIAN 2: Tambahkan options untuk lokasi (setelah posisiOptions, sekitar baris ~280)
    const lokasiOptions = [
        { value: 'Jakarta', label: 'Jakarta' },
        { value: 'Makassar', label: 'Makassar' },
        { value: 'Kendari', label: 'Kendari' },
        { value: 'Surabaya', label: 'Surabaya' },
        { value: 'Medan', label: 'Medan' },
        { value: 'Palopo', label: 'Palopo' },
        { value: 'Malili', label: 'Malili' },
        { value: 'Bitung', label: 'Bitung' },
        // Tambahkan lokasi lainnya sesuai kebutuhan
    ]

    // Fetch reviewers from Firestore
    const fetchReviewers = async () => {
        try {
            const q = query(collection(db, 'users'), where('role', '==', 'Reviewer'))
            const querySnapshot = await getDocs(q)

            const reviewers = querySnapshot.docs.map((doc) => ({
                value: doc.data().nama,
                label: doc.data().nama,
                uid: doc.data().uid
            }))

            setReviewerOptions(reviewers)
        } catch (error) {
            console.error('Error fetching reviewer options:', error)
        }
    }

    // Fetch validators from Firestore
    const fetchValidators = async () => {
        try {
            const q = query(collection(db, 'users'),
                where('role', 'in', ['Validator', 'Reviewer', 'Admin']))
            const querySnapshot = await getDocs(q)

            const validators = querySnapshot.docs.map((doc) => ({
                value: doc.data().nama,
                label: doc.data().nama,
                uid: doc.data().uid
            }))

            setValidatorOptions(validators)
        } catch (error) {
            console.error('Error fetching validator options:', error)
        }
    }

    useEffect(() => {
        fetchReviewers()
        fetchValidators()
    }, [])

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value
        })
    }

    const handleSelectChange = (selectedOption, field) => {
        if (field === 'role' && selectedOption?.value === 'Super Admin') {
            setFormData({
                ...formData,
                role: 'Super Admin',
                unit: [],
                posisi: '',
                department: [],
                bankName: '',
                accountNumber: '',
                reviewer1: [],
                reviewer2: [],
                validator: [],
                lokasi: [],
                platKendaraan: [],
                [field]: selectedOption.value
            })
        } else if (field === 'department' || field === 'unit' || field === 'lokasi') {
            setFormData({
                ...formData,
                [field]: Array.isArray(selectedOption)
                    ? selectedOption.map((option) => option.value)
                    : []
            })
        } else {
            setFormData({
                ...formData,
                [field]: Array.isArray(selectedOption)
                    ? selectedOption.map((option) => option.uid)
                    : selectedOption?.value || ''
            })
        }
    }

    const handlePlatChange = (selectedOptions) => {
        const values = Array.isArray(selectedOptions)
            ? selectedOptions
                .map((option) => (option.value || '').toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim())
                .filter(Boolean)
            : []
        setFormData({ ...formData, platKendaraan: values })
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const missingFields = [];
            let fieldsToValidate = [];

            if (formData.role === 'Super Admin') {
                fieldsToValidate = [
                    { name: 'nama', label: 'Nama' },
                    { name: 'email', label: 'Email' },
                    { name: 'role', label: 'Role' }
                ];
            } else if (formData.role === 'Reviewer') {
                fieldsToValidate = [
                    { name: 'nama', label: 'Nama' },
                    { name: 'email', label: 'Email' },
                    { name: 'posisi', label: 'Posisi' },
                    { name: 'unit', label: 'Unit Bisnis' },
                    { name: 'role', label: 'Role' },
                    { name: 'department', label: 'Department' },
                    { name: 'bankName', label: 'Nama Bank' },
                    { name: 'accountNumber', label: 'Nomor Rekening' },
                    { name: 'lokasi', label: 'Lokasi' }
                ];
            } else {
                fieldsToValidate = [
                    { name: 'nama', label: 'Nama' },
                    { name: 'email', label: 'Email' },
                    { name: 'posisi', label: 'Posisi' },
                    { name: 'unit', label: 'Unit Bisnis' },
                    { name: 'role', label: 'Role' },
                    { name: 'department', label: 'Department' },
                    { name: 'bankName', label: 'Nama Bank' },
                    { name: 'accountNumber', label: 'Nomor Rekening' },
                    { name: 'reviewer1', label: 'Reviewer 1' },
                    { name: 'reviewer2', label: 'Reviewer 2' },
                    { name: 'validator', label: 'Validator' },
                    { name: 'lokasi', label: 'Lokasi' }
                ];
            }

            for (let field of fieldsToValidate) {
                if (!formData[field.name] || (Array.isArray(formData[field.name]) && formData[field.name].length === 0)) {
                    missingFields.push(field.label);
                }
            }

            if (missingFields.length > 0) {
                console.warn('Field yang tidak diisi:', missingFields);
                missingFields.forEach((field) => {
                    toast.warning(
                        <>
                            <b>{field}</b> tidak boleh kosong
                        </>
                    );
                });
                setIsSubmitting(false);
                return;
            }

            // Validasi reviewer1 dan reviewer2 tidak boleh sama
            if (formData.reviewer1?.length > 0 && formData.reviewer2?.length > 0) {
                if (formData.reviewer1.some((r) => formData.reviewer2.includes(r))) {
                    console.warn('Reviewer 1 dan Reviewer 2 sama');
                    toast.warning('Reviewer 1 dan Reviewer 2 tidak boleh sama');
                    setIsSubmitting(false);
                    return;
                }
            }

            try {
                const createManagedUser = httpsCallable(functions, 'createManagedUser');
                await createManagedUser({
                    nama: formData.nama,
                    email: formData.email,
                    role: formData.role,
                    posisi: formData.role === 'Super Admin' ? '' : formData.posisi,
                    unit: formData.role === 'Super Admin' ? [] : formData.unit,
                    department: formData.role === 'Super Admin' ? [] : formData.department,
                    bankName: formData.role === 'Super Admin' ? '' : formData.bankName,
                    accountNumber: formData.role === 'Super Admin' ? '' : formData.accountNumber,
                    reviewer1: formData.role === 'Super Admin' ? [] : formData.reviewer1,
                    reviewer2: formData.role === 'Super Admin' ? [] : formData.reviewer2,
                    validator: formData.role === 'Super Admin' ? [] : formData.validator,
                    lokasi: formData.role === 'Super Admin' ? [] : formData.lokasi,
                    platKendaraan: formData.role === 'Super Admin' ? [] : formData.platKendaraan,
                });

                toast.success('Pengguna berhasil ditambahkan. Email pengaturan kata sandi akan dikirim jika email notifikasi aktif.');

                // Reset form setelah submit
                setFormData({
                    nama: '',
                    email: '',
                    posisi: null,
                    unit: [],
                    role: null,
                    department: [],
                    bankName: '',
                    accountNumber: '',
                    reviewer1: [],
                    reviewer2: [],
                    validator: [],
                    lokasi: [],
                    platKendaraan: []
                });

                setTimeout(() => {
                    navigate(-1)
                }, 3500)
            } catch (firebaseError) {
                console.error('Detailed Firebase Error:', firebaseError);

                // More specific error messages
                if (firebaseError.code === 'functions/already-exists') {
                    toast.error('Email sudah terdaftar. Gunakan email lain.');
                } else if (firebaseError.code === 'functions/invalid-argument') {
                    toast.error(firebaseError.message || 'Data pengguna tidak valid.');
                } else if (firebaseError.code === 'functions/permission-denied') {
                    toast.error('Anda tidak memiliki akses untuk menambahkan pengguna.');
                } else if (firebaseError.code === 'auth/invalid-email') {
                    toast.error('Format email tidak valid.');
                } else {
                    toast.error(`Gagal menambahkan pengguna: ${firebaseError.message}`);
                }
            }
        } catch (error) {
            console.error('Error terjadi saat proses submit:', error);
            toast.error('Gagal menambahkan pengguna. Silakan coba lagi');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Options role
    const roleOptions = [
        { value: 'Employee', label: 'Employee' },
        { value: 'Validator', label: 'Validator' },
        { value: 'Reviewer', label: 'Reviewer' },
        { value: 'Admin', label: 'Admin' },
        { value: 'Super Admin', label: 'Super Admin' }
    ]

    // Options unit
    const unitOptions = [
        { value: 'PT Makassar Jaya Samudera', label: 'PT Makassar Jaya Samudera' },
        { value: 'PT Samudera Makassar Logistik', label: 'PT Samudera Makassar Logistik' },
        { value: 'PT Kendari Jaya Samudera', label: 'PT Kendari Jaya Samudera' },
        { value: 'PT Samudera Kendari Logistik', label: 'PT Samudera Kendari Logistik' },
        { value: 'PT Samudera Agencies Indonesia', label: 'PT Samudera Agencies Indonesia' },
        { value: 'PT SILKargo Indonesia', label: 'PT SILKargo Indonesia' },
        { value: 'PT PAD Samudera Perdana', label: 'PT PAD Samudera Perdana' },
        { value: 'PT Masaji Kargosentra Tama', label: 'PT Masaji Kargosentra Tama' },
        { value: 'Samudera Indonesia', label: 'Samudera Indonesia' },
        { value: 'Panitia SISCO', label: 'Panitia SISCO' },
        { value: 'Panitia', label: 'Panitia' }
    ]

    // Options department
    const departmentOptions = [
        { value: 'Operation', label: 'Operation' },
        { value: 'Marketing', label: 'Marketing' },
        { value: 'Finance', label: 'Finance' },
        { value: 'GA/Umum', label: 'GA/Umum' },
        { value: 'HC', label: 'HC' },
        { value: 'QHSE', label: 'QHSE' },
        { value: 'VMS', label: 'VMS' },
        { value: 'IT', label: 'IT' },
        { value: 'Panitia', label: 'Panitia' },
    ]

    // Options posisi
    const posisiOptions = [
        { value: 'Staff', label: 'Staff' },
        { value: 'Section Head', label: 'Section Head' },
        { value: 'Department Head', label: 'Department Head' },
        { value: 'General Manager', label: 'General Manager' },
        { value: 'Direktur', label: 'Direktur' }
    ]

    const selectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            '&:hover': {
                borderColor: '#3b82f6'
            },
            minHeight: '32px',
            fontSize: '14px',
            display: 'flex',
            flexWrap: 'nowrap',
            overflow: 'auto'
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            overflow: 'auto',
            '::-webkit-scrollbar': {
                display: 'none'
            },
            scrollbarWidth: 'none'
        }),
        singleValue: (base) => ({
            ...base,
            color: isDark ? '#f3f4f6' : '#111827'
        }),
        input: (base) => ({
            ...base,
            color: isDark ? '#f3f4f6' : '#111827'
        }),
        placeholder: (base) => ({
            ...base,
            color: isDark ? '#9ca3af' : '#6b7280'
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            zIndex: 100
        }),
        // Bagian AX: menuPortalTarget={document.body} butuh z-index tinggi di
        // sini juga (bukan cuma di `menu`) supaya dropdown tidak tampil
        // tertutup/salah posisi kalau Select ini dirender di dalam modal lain.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? (isDark ? '#374151' : '#f3f4f6')
                : (isDark ? '#1f2937' : '#ffffff'),
            color: isDark ? '#f3f4f6' : '#111827',
            fontSize: '14px',
            padding: '6px 12px',
            cursor: 'pointer'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDark ? '#374151' : '#e5e7eb',
            fontSize: '14px',
            flexShrink: 0
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDark ? '#f3f4f6' : '#111827'
        }),
    }

    return (
        <div className="container mx-auto py-10 md:py-8 md:pb-20">
            <h2 className="text-xl font-bold mb-4 dark:text-gray-100">Manage Users</h2>

            <div className="bg-white p-6 rounded-lg shadow dark:bg-gray-800">
                <h3 className="text-xl font-medium mb-4 dark:text-gray-100">Tambah Pengguna</h3>
                <form onSubmit={handleSubmit}>
                    <div className='hidden sm:block'>
                        <div className="sm:grid sm:grid-cols-2 gap-6">
                            <div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Nama Lengkap <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="nama"
                                        value={formData.nama}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Email <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                    />
                                </div>
                            </div>
                            {formData.role !== 'Super Admin' && (
                                <div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Nama Bank <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="bankName"
                                            value={formData.bankName}
                                            onChange={handleChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Nomor Rekening <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="accountNumber"
                                            value={formData.accountNumber}
                                            onChange={handleChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.role !== 'Super Admin' && (
                            <div className="sm:grid sm:grid-cols-2 gap-6">
                                <div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Unit Bisnis <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <Select
                                            isMulti
                                            name="unit"
                                            options={unitOptions}
                                            value={formData.unit.length > 0 ? unitOptions.filter(option => formData.unit.includes(option.value)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'unit')}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Lokasi <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <Select
                                            isMulti
                                            name="lokasi"
                                            options={lokasiOptions}
                                            value={formData.lokasi.length > 0 ? lokasiOptions.filter(option => formData.lokasi.includes(option.value)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'lokasi')}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Department <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <Select
                                            isMulti
                                            name="department"
                                            options={departmentOptions}
                                            value={formData.department.length > 0 ? departmentOptions.filter(option => formData.department.includes(option.value)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'department')}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Plat No Kendaraan
                                        </label>
                                        <CreatableSelect
                                            isMulti
                                            name="platKendaraan"
                                            options={[]}
                                            value={formData.platKendaraan.map(p => ({ value: p, label: p }))}
                                            onChange={handlePlatChange}
                                            placeholder="Isi jika karyawan memiliki kendaraan (mis. DD 1234 AB)"
                                            formatCreateLabel={(input) => `Tambah plat "${input.toUpperCase()}"`}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Validator {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                        </label>
                                        <Select
                                            isMulti
                                            name="validator"
                                            options={validatorOptions}
                                            value={formData.validator.length > 0 ? validatorOptions.filter(option => formData.validator.includes(option.uid)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'validator')}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Reviewer 1 {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                        </label>
                                        <Select
                                            isMulti
                                            name="reviewer1"
                                            options={reviewerOptions}
                                            value={formData.reviewer1.length > 0 ? reviewerOptions.filter(option => formData.reviewer1.includes(option.uid)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'reviewer1')}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="sm:grid sm:grid-cols-2 gap-6">
                            <div>
                                {formData.role !== 'Super Admin' && (
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Posisi <span className="text-red-500 dark:text-red-400">*</span>
                                        </label>
                                        <Select
                                            name="posisi"
                                            options={posisiOptions}
                                            value={formData.posisi ? posisiOptions.find(option => option.value === formData.posisi) : null}
                                            className="basic-single-select mt-1 hover:border-blue-400"
                                            classNamePrefix="select"
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'posisi')}
                                            isClearable
                                            styles={selectStyles}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                )}
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Role <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <Select
                                        name="role"
                                        options={roleOptions}
                                        value={formData.role ? roleOptions.find(option => option.value === formData.role) : null}
                                        className="basic-single-select mt-1 hover:border-blue-400"
                                        classNamePrefix="select"
                                        onChange={(selectedOption) => handleSelectChange(selectedOption, 'role')}
                                        isMulti={false}
                                        isClearable
                                        styles={selectStyles}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                            </div>
                            <div>
                                {formData.role !== 'Super Admin' && (
                                    <div className="mb-2">
                                        <label className="block font-medium text-gray-700 dark:text-gray-300">
                                            Reviewer 2 {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                        </label>
                                        <Select
                                            isMulti
                                            name="reviewer2"
                                            options={reviewerOptions}
                                            value={formData.reviewer2.length > 0 ? reviewerOptions.filter(option => formData.reviewer2.includes(option.uid)) : []}
                                            className="basic-multi-select mt-1"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'reviewer2')}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile/small screen layout */}
                    <div className="block sm:hidden">
                        <div className="mb-2">
                            <label className="block font-medium text-gray-700 dark:text-gray-300">
                                Nama Lengkap <span className="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                name="nama"
                                value={formData.nama}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                            />
                        </div>
                        <div className="mb-2">
                            <label className="block font-medium text-gray-700 dark:text-gray-300">
                                Email <span className="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                            />
                        </div>
                        <div className="mb-2">
                            <label className="block font-medium text-gray-700 dark:text-gray-300">
                                Role <span className="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <Select
                                name="role"
                                options={roleOptions}
                                value={formData.role ? roleOptions.find(option => option.value === formData.role) : null}
                                className="basic-single-select mt-1 hover:border-blue-400"
                                classNamePrefix="select"
                                onChange={(selectedOption) => handleSelectChange(selectedOption, 'role')}
                                isMulti={false}
                                isClearable
                                styles={selectStyles}
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                menuPosition="absolute"
                            />
                        </div>
                        {formData.role !== 'Super Admin' && (
                            <div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Posisi <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <Select
                                        name="posisi"
                                        options={posisiOptions}
                                        value={formData.posisi ? posisiOptions.find(option => option.value === formData.posisi) : null}
                                        onChange={(selectedOption) => handleSelectChange(selectedOption, 'posisi')}
                                        className="basic-single-select mt-1 hover:border-blue-400"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Department <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <Select
                                        isMulti
                                        name="department"
                                        options={departmentOptions}
                                        value={formData.department.length > 0 ? departmentOptions.filter(option => formData.department.includes(option.value)) : []}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'department')}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Unit Bisnis <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <Select
                                        isMulti
                                        name="unit"
                                        options={unitOptions}
                                        value={formData.unit.length > 0 ? unitOptions.filter(option => formData.unit.includes(option.value)) : []}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'unit')}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Lokasi <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <Select
                                        isMulti
                                        name="lokasi"
                                        options={lokasiOptions}
                                        value={formData.lokasi.length > 0 ? lokasiOptions.filter(option => formData.lokasi.includes(option.value)) : []}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'lokasi')}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Plat No Kendaraan
                                    </label>
                                    <CreatableSelect
                                        isMulti
                                        name="platKendaraan"
                                        options={[]}
                                        value={formData.platKendaraan.map(p => ({ value: p, label: p }))}
                                        onChange={handlePlatChange}
                                        placeholder="Isi jika karyawan memiliki kendaraan (mis. DD 1234 AB)"
                                        formatCreateLabel={(input) => `Tambah plat "${input.toUpperCase()}"`}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Nama Bank <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="bankName"
                                        value={formData.bankName}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Nomor Rekening <span className="text-red-500 dark:text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="accountNumber"
                                        value={formData.accountNumber}
                                        onChange={handleChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 hover:border-blue-400 focus:ring-blue-500 focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Validator {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                    </label>
                                    <Select
                                        isMulti
                                        name="validator"
                                        options={validatorOptions}
                                        value={formData.validator.length > 0 ? validatorOptions.filter(option => formData.validator.includes(option.uid)) : []}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'validator')}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Reviewer 1 {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                    </label>
                                    <Select
                                        isMulti
                                        name="reviewer1"
                                        options={reviewerOptions}
                                        value={formData.reviewer1.length > 0 ? reviewerOptions.filter(option => formData.reviewer1.includes(option.uid)) : []}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'reviewer1')}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block font-medium text-gray-700 dark:text-gray-300">
                                        Reviewer 2 {formData.role !== 'Reviewer' && <span className="text-red-500 dark:text-red-400">*</span>}
                                    </label>
                                    <Select
                                        isMulti
                                        name="reviewer2"
                                        options={reviewerOptions}
                                        value={formData.reviewer2.length > 0 ? reviewerOptions.filter(option => formData.reviewer2.includes(option.uid)) : []}
                                        className="basic-multi-select mt-1"
                                        classNamePrefix="select"
                                        styles={selectStyles}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'reviewer2')}
                                        menuPortalTarget={document.body}
                                        menuPosition="absolute"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end mt-6 gap-4">
                        <button onClick={() => navigate(-1)} className="px-16 py-3 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 hover:text-gray-700 w-full sm:w-auto dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600" disabled={isSubmitting}>Cancel</button>
                        <button type="submit" className="px-16 py-3 bg-red-600 text-white rounded hover:bg-red-700 hover:text-gray-200 w-full sm:w-auto" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save'}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddUserForm